import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { templateConfigMap, templateDisplayOrder, TemplateKey } from './poll-config/template.config';
import { UIMesageService } from '../../../shared/services/ui-message.service';
import defaultOptionsEn from '../../../../assets/locale/en/default-options.en.json';
import defaultOptionsDe from '../../../../assets/locale/de/default-options.de.json';
import defaultOptionsIt from '../../../../assets/locale/it/default-options.it.json';
import defaultOptionsFr from '../../../../assets/locale/fr/default-options.fr.json';
import defaultOptionsPt from '../../../../assets/locale/pt/default-options.pt.json';
import defaultOptionsEs from '../../../../assets/locale/es/default-options.es.json';

import { HeaderData, OptionConfigClass, OptionList, OptionListPersistence, TemplateSettingFlags } from 'whocan-lib';
import { EssErrorService, EssTranslationService } from 'ngx-essentia';
import { OptionData } from 'whocan-lib';
import { HashMap, TranslocoService } from '@jsverse/transloco';

@Injectable({
    providedIn: 'root'
})
/**
 *
 */
export class TemplateService {
    private fallbackShowKeys = false
    public templateKey: string = 'advanced'
    private getConfigAttributeCallCounter = 0
    private getDefaultSettingsCallCounter = 0
    public defaultOptions: any = null;

    constructor(
        private logger: LoggerService,
        private trans: EssTranslationService,
        private uiMessage: UIMesageService,
        private errorService: EssErrorService,
        @Inject(LOCALE_ID) private locale: string,
        private transLoco: TranslocoService


    ) {
        this.initDefaultOptions()
    }



    public getTemplateHierarchy(templateKey?: string): string[] {
        if (!templateKey) templateKey = this.templateKey
        return this.getTemplateHierarchyReverse(templateKey).reverse()
    }

    private getTemplateHierarchyReverse(templateKey: string): string[] {
        let returnValue = templateKey ? [templateKey] : []
        const inheritFrom = this.getTemplateConfig(templateKey)?.general.inheritFrom
        if (inheritFrom && inheritFrom !== templateKey) {
            returnValue = [...returnValue, ...this.getTemplateHierarchyReverse(inheritFrom)] // recusrsive call with inherit from
        }
        return (returnValue) // reverse the array to make it easy to pop
    }

    private initDefaultOptions() {
        if (this.locale === 'de') {
            this.defaultOptions = defaultOptionsDe
        } else if (this.locale === 'pt') {
            this.defaultOptions = defaultOptionsPt
        } else if (this.locale === 'it') {
            this.defaultOptions = defaultOptionsIt
        } else if (this.locale === 'fr') {
            this.defaultOptions = defaultOptionsFr
        } else if (this.locale === 'es') {
            this.defaultOptions = defaultOptionsEs
        } else {// the default is english
            this.defaultOptions = defaultOptionsEn
        }
    }

    private getDefaultOptionListData(defaultOptionsKey: string): OptionListPersistence {
        let returnValue = this.defaultOptions[defaultOptionsKey]
        if (!returnValue) {
            returnValue = defaultOptionsEn[defaultOptionsKey];
        }
        return returnValue
    }

    /**
     *
     * @returns a deep copy of the default options
     */
    public getDefaultOptionList(timeZone: string): OptionList {
        let optionsData: OptionData[] | OptionListPersistence;
        const defaultOptionsKey = <string>this.getConfigAttribute('general', 'defaultOptions');
        if (defaultOptionsKey) {
            optionsData = this.getDefaultOptionListData(defaultOptionsKey)
            this.logger.log('defaultOptionsData', optionsData)
        }
        return (new OptionList(optionsData, timeZone)).deepCopy(); // need the deep copy to avoid changes made to the option would effect the next poll created
    }

    public initTemplate(templateKey: string) {
        this.templateKey = this.getPollTemplate(templateKey);
    }

    public getTemplateKeys(getAll: boolean = false): string[] {
        if (getAll) {
            const returnValue: string[] = [];
            templateConfigMap.forEach((config, key) => {
                returnValue.push(<TemplateKey>key);
            });
            return returnValue;
        }
        return [...templateDisplayOrder];
    }

    public getDefaultSettings(templateKey?: string): HeaderData {
        //      this.logger.log('getConfigAttribute start', attributeKeyLevel1, attributeKeyLevel2, templateKey)
        let templateKeyWork = templateKey;
        if (!templateKeyWork) {
            templateKeyWork = this.templateKey
        }
        if (!templateKeyWork) {
            templateKeyWork = TemplateKey.advanced
            this.logger.log('getDefaultSettings, template not initialized')
        }
        this.getDefaultSettingsCallCounter = 0
        try {
            const returnValue = this.getDefaultSettingsRecursive(templateKeyWork)
            return returnValue

        } catch (error) {
            this.logger.error(error)
            return
        }
    }


    /**
     * Walks up the inheritFrom chain and ends at the root template (advanced): if even the
     * root has no defaultSettings the result is undefined. The call counter only guards
     * against a broken config (cycle in inheritFrom) - there a thrown error is right.
     */
    private getDefaultSettingsRecursive(templateKey: string): HeaderData | undefined {
        const attributeKeyLevel1 = 'defaultSettings'
        this.getDefaultSettingsCallCounter++
        // make sure we are not in an endless recursive loop
        if (this.getDefaultSettingsCallCounter > 10) {
            throw this.errorService.newError('Error getting attribute: ' + templateKey + "/" + attributeKeyLevel1)
        }
        let returnValue = this.getTemplateConfig(templateKey)[attributeKeyLevel1]
        if (returnValue === undefined) {
            this.logger.log('getDefaultSettingsRecursive; undefined', attributeKeyLevel1, templateKey)
            const inheritFrom = this.getTemplateConfig(templateKey)?.general.inheritFrom
            if (inheritFrom) {
                this.logger.log('getDefaultSettingsRecursive; inheritFrom', attributeKeyLevel1, templateKey)
                returnValue = this.getDefaultSettingsRecursive(inheritFrom)
            }
            else if (templateKey !== TemplateKey.advanced) {
                this.logger.log('getDefaultSettingsRecursive; NOT inheritFrom', attributeKeyLevel1, templateKey)
                returnValue = this.getDefaultSettingsRecursive(TemplateKey.advanced)
            }
            // else: root template reached without a hit -> undefined
        }
        return returnValue
    }

    /**
     * Whether location and meeting link sit behind their toggle in the advanced section (availableSettings
     * locationAdvanced / meetingLinkAdvanced of the current or the given template) and whether the template offers the
     * meeting link at all (any of its three flags; not invitation, potluck, duties - C2) - the input of headerLocation,
     * headerMeetingLink and calendarEventsFor in whocan-lib, the one visibility rule of vote page, calendar and
     * mail (calendar quick wins C, task 0). The functions read the same flags from the label generator.
     */
    public templateSettingFlags(templateKey?: string): TemplateSettingFlags {
        const isOn = (attribute: string) => this.getConfigAttribute('availableSettings', attribute, templateKey) === true;
        return {
            locationAdvanced: isOn('locationAdvanced'),
            meetingLinkAdvanced: isOn('meetingLinkAdvanced'),
            meetingLinkAvailable: isOn('meetingLinkFirstStep') || isOn('meetingLink') || isOn('meetingLinkAdvanced'),
        };
    }

    /**
     * Resolves a config attribute along the inheritFrom chain. Returns undefined when the
     * attribute is defined nowhere in the chain (a legitimate state for optional attributes
     * such as defaultSettingsIfRegistered); '' only for a real config error (logged).
     */
    public getConfigAttribute(attributeKeyLevel1: string, attributeKeyLevel2: string, templateKey?: string): string | boolean | string[] | undefined {
        //      this.logger.log('getConfigAttribute start', attributeKeyLevel1, attributeKeyLevel2, templateKey)
        let templateKeyWork = templateKey;
        if (!templateKeyWork) {
            templateKeyWork = this.templateKey
        }
        if (!templateKeyWork) {
            templateKeyWork = TemplateKey.advanced
            this.logger.log('getConfigAttribute, template not initialized' + attributeKeyLevel1 + "/" + attributeKeyLevel2)
        }
        this.getConfigAttributeCallCounter = 0
        try {
            const returnValue = this.getConfigAttributeRecursive(attributeKeyLevel1, attributeKeyLevel2, templateKeyWork)
            return returnValue

        } catch (error) {
            this.logger.error(error)
            return ('')
        }
    }

    //      in order to acces translation not asynchronously
    public async loadTransLationFiles() {
        const templateKeys = this.getTemplateKeys(true);
        const loadPromises = templateKeys.map(key => this.trans.load('template/' + key));
        await Promise.all(loadPromises);
    }
    /**
     *
     * @param attributeKeyLevel1
     * @param attributeKeyLevel2
     * @param templateKey
     * @param params e.g. : { difference: "+2" }
     * @returns
     */
    public getLabel(attributeKeyLevel1: string, attributeKeyLevel2: string, templateKey?: string, params?: HashMap) {
        const templateHierarchy = this.getTemplateHierarchy(templateKey)
        const returnValue = this.trans.translate(attributeKeyLevel1 + '.' + attributeKeyLevel2, 'template', templateHierarchy, params)
        return returnValue
    }

    /**
     * Like getLabel(), but '' when no dictionary of the template hierarchy has the key.
     * For a missing key transloco hands back the key path ("template/advanced.configurableOption.x"),
     * so a plain falsy check would let that string through as if it were a label.
     */
    public getOptionalLabel(attributeKeyLevel1: string, attributeKeyLevel2: string, templateKey?: string): string {
        const key = attributeKeyLevel1 + '.' + attributeKeyLevel2
        const label = this.getLabel(attributeKeyLevel1, attributeKeyLevel2, templateKey)
        if (!label || label === key || label.endsWith('.' + key)) return ''
        return label
    }


    private getConfigAttributeRecursive(attributeKeyLevel1: string, attributeKeyLevel2: string, templateKey: string): string | boolean | string[] | undefined {
        this.getConfigAttributeCallCounter++
        // make sure we are not in an endless recursive loop (only reachable with a cycle in inheritFrom)
        if (this.getConfigAttributeCallCounter > 10) {
            const errorMessage = ('Error getting attribute: ' + templateKey + "/" + attributeKeyLevel1 + "/" + attributeKeyLevel2)
            throw (new Error(errorMessage))
        }
        let returnValue = this.getTemplateConfig(templateKey)[attributeKeyLevel1]?.[attributeKeyLevel2]
        if (returnValue === undefined) {
            const inheritFrom = this.getTemplateConfig(templateKey)?.general.inheritFrom
            if (inheritFrom) {
                returnValue = this.getConfigAttributeRecursive(attributeKeyLevel1, attributeKeyLevel2, inheritFrom)
            }
            else if (templateKey !== TemplateKey.advanced) {
                this.logger.log('getConfigAttributeRecursive; NOT inheritFrom', attributeKeyLevel1, attributeKeyLevel2, templateKey)
                returnValue = this.getConfigAttributeRecursive(attributeKeyLevel1, attributeKeyLevel2, TemplateKey.advanced)
            }
            // else: root template (advanced) reached without a hit -> undefined, not an error
        }
        return returnValue
    }

    private getTemplateConfig(templateKey: string) {
        let returnValue = templateConfigMap.get(templateKey)
        if (returnValue === undefined || returnValue === null) {
            returnValue = templateConfigMap.get(TemplateKey.advancedNew)
        }
        return returnValue

    }

    /**
     *  make sure the template that we goot is valid and if not set template to advanced
     * @param templateFromRoute
     * @returns
     */
    public getPollTemplate(templateFromRoute: string): string {
        let templateKey: string;
        templateConfigMap.forEach((config, key) => {
            if (key === templateFromRoute) {
                templateKey = <TemplateKey>key;
            }
        })
        if (templateKey) {
            return templateKey
        } else {
            return TemplateKey.advanced
        }
    }

    public getIcon(templateKey?: string): string {
        return <string>this.getConfigAttribute('general', 'icon', templateKey)
    }

    getOptionConfigKeys(filterAllowToAddOptions: boolean = false): string[] {
        let returnValue = []
        let optionTypes = this.getConfigAttribute('options', 'optionTypes')
        if (Array.isArray(optionTypes)) {
            returnValue = <string[]>optionTypes
        }
        if (filterAllowToAddOptions) {
            returnValue = returnValue.filter((optionConfigKey) => {
                const optionConfig = OptionConfigClass.newOptionConfigClass(optionConfigKey)
                if (!optionConfig) throw (this.errorService.newError('getOptionConfigKeys - no such optionConfig' + optionConfigKey))
                return optionConfig.allowToAddOptions
            })
        }
        return returnValue
    }

    getDateOptionConfigKeys(filterAllowToAddOptions: boolean = false): string[] {
        let returnValue = this.getOptionConfigKeys(filterAllowToAddOptions)
        returnValue = returnValue.filter(optionConfigKey => {
            const optionConfig = OptionConfigClass.newOptionConfigClass(optionConfigKey)
            if (!optionConfig) throw (this.errorService.newError('getDateOptionConfigKeys - no such optionConfig' + optionConfigKey))
            return optionConfig.isOfTypeDate
        })
        return returnValue
    }


    getOptionConfigKeyLabel(configKey: string): string {
        let returnValue = this.getLabel('configurableOption', configKey + '_buttonLabel')
        if (!returnValue) {
            this.logger.error('No Button Label for optionType: ' + configKey + '_buttonLabel')
            returnValue = returnValue
        }
        return returnValue
    }
}
